/**
 * Suppliers - who the shop buys from.
 *
 * The list behind the picker on the purchase screen. A delivery cannot be
 * recorded against a supplier that does not exist, so this is where one is
 * added before the first purchase from them rather than through the database.
 *
 * A supplier is retired, never deleted: the purchases that name it are still on
 * the books and the balance may still be owed. Deactivating takes it out of the
 * pickers and off the list; ticking Active again brings it back.
 *
 * The balance is read-only here on purpose. It is the running total of the
 * purchases and payments behind it, so it moves on the Purchases and Accounts
 * screens - a figure typed over it would only disagree with the ledger.
 */

import { useState, useCallback } from 'react';
import {
    FiPlus, FiSearch, FiEdit2, FiTruck, FiSlash, FiUsers, FiDollarSign
} from 'react-icons/fi';
import toast from 'react-hot-toast';

import { inventoryApi } from '../services/endpoints';
import useAsync, { useAction } from '../hooks/useAsync';
import DataTable from '../components/ui/DataTable';
import Modal from '../components/ui/Modal';
import { PageHeader, StatCard, Badge } from '../components/ui/Feedback';
import { Field, Input, NumberInput, Textarea, Toggle } from '../components/ui/Field';
import { money, formatDate } from '../utils/format';

/** A new supplier, before anything is typed into it. */
const blankSupplier = () => ({
    supplierCode: '',
    supplierName: '',
    contactPerson: '',
    mobileNo: '',
    phoneNo: '',
    email: '',
    address: '',
    city: '',
    ntn: '',
    strn: '',
    openingBalance: 0,
    creditDays: 0,
    isActive: true
});

/** The row as the table has it, turned back into what the form edits. */
const toForm = (row) => ({
    Supplier_ID: row.Supplier_ID,
    supplierCode: row.Supplier_Code || '',
    supplierName: row.Supplier_Name || '',
    contactPerson: row.Contact_Person || '',
    mobileNo: row.Mobile_No || '',
    phoneNo: row.Phone_No || '',
    email: row.Email || '',
    address: row.Address || '',
    city: row.City || '',
    ntn: row.NTN || '',
    strn: row.STRN || '',
    openingBalance: Number(row.Opening_Balance) || 0,
    creditDays: Number(row.Credit_Days) || 0,
    isActive: row.Is_Active !== false && row.Is_Active !== 0
});

/** Empty text boxes are absent values, not empty strings, to the API. */
const trimmed = (value) => {
    const text = String(value ?? '').trim();
    return text === '' ? null : text;
};

const SuppliersPage = () => {
    const [filters, setFilters] = useState({ search: '', includeInactive: false });
    const [editing, setEditing] = useState(null);

    const load = useCallback(
        () => inventoryApi.listSuppliers({
            search: filters.search || undefined,
            includeInactive: filters.includeInactive
        }),
        [filters]
    );
    const { data: suppliers, loading, refetch } = useAsync(load, [filters]);

    const { run: save, busy: saving } = useAction(
        (form) => {
            const payload = {
                supplierCode: trimmed(form.supplierCode),
                supplierName: String(form.supplierName).trim(),
                contactPerson: trimmed(form.contactPerson),
                mobileNo: trimmed(form.mobileNo),
                phoneNo: trimmed(form.phoneNo),
                email: trimmed(form.email) || '',
                address: trimmed(form.address),
                city: trimmed(form.city),
                ntn: trimmed(form.ntn),
                strn: trimmed(form.strn),
                creditDays: Number(form.creditDays) || 0,
                isActive: !!form.isActive
            };

            // The opening balance is what was owed before the shop started
            // recording purchases here, so it is only meaningful on the way in.
            return form.Supplier_ID
                ? inventoryApi.updateSupplier(form.Supplier_ID, payload)
                : inventoryApi.createSupplier({
                    ...payload,
                    openingBalance: Number(form.openingBalance) || 0
                });
        },
        {
            successMessage: 'Supplier saved.',
            onSuccess: () => { setEditing(null); refetch(); }
        }
    );

    const { run: deactivate } = useAction(
        (row) => inventoryApi.deactivateSupplier(row.Supplier_ID),
        { successMessage: 'Supplier deactivated.', onSuccess: refetch }
    );

    const rows = suppliers || [];
    const owed = rows.reduce((sum, row) => sum + Math.max(Number(row.Current_Balance) || 0, 0), 0);
    const owedCount = rows.filter((row) => Number(row.Current_Balance) > 0).length;

    const columns = [
        {
            key: 'Supplier_Name',
            header: 'Supplier',
            render: (row) => (
                <div className="min-w-0">
                    <p className="truncate font-medium text-slate-800">
                        {row.Supplier_Name}
                        {!row.Is_Active && (
                            <Badge tone="slate" className="ml-2">inactive</Badge>
                        )}
                    </p>
                    <p className="truncate text-xs text-slate-500">
                        {row.Contact_Person || 'No contact named'}
                        {row.Supplier_Code && ` · ${row.Supplier_Code}`}
                    </p>
                </div>
            )
        },
        {
            key: 'Mobile_No',
            header: 'Reach them on',
            render: (row) => (
                <div className="min-w-0">
                    <p className="truncate text-slate-700">{row.Mobile_No || row.Phone_No || '—'}</p>
                    {row.Email && <p className="truncate text-xs text-slate-500">{row.Email}</p>}
                </div>
            )
        },
        {
            key: 'City',
            header: 'City',
            render: (row) => <span className="text-slate-600">{row.City || '—'}</span>
        },
        {
            key: 'Credit_Days',
            header: 'Terms',
            align: 'right',
            render: (row) => (
                <span className="text-slate-600">
                    {Number(row.Credit_Days) > 0 ? `${row.Credit_Days} days` : 'On delivery'}
                </span>
            )
        },
        {
            key: 'Purchase_Count',
            header: 'Deliveries',
            align: 'right',
            render: (row) => (
                <div>
                    <p className="text-slate-700">{row.Purchase_Count || 0}</p>
                    {row.Last_Purchase_Date && (
                        <p className="text-xs text-slate-500">last {formatDate(row.Last_Purchase_Date)}</p>
                    )}
                </div>
            )
        },
        {
            key: 'Current_Balance',
            header: 'Owed',
            align: 'right',
            render: (row) => (Number(row.Current_Balance) > 0
                ? <span className="font-medium text-amber-700">{money(row.Current_Balance)}</span>
                : <span className="text-slate-400">—</span>)
        },
        {
            key: 'actions',
            header: '',
            render: (row) => (
                <div className="flex justify-end gap-1">
                    <button
                        type="button"
                        className="btn-ghost btn-sm !px-2"
                        onClick={(event) => { event.stopPropagation(); setEditing(toForm(row)); }}
                        title="Edit"
                    >
                        <FiEdit2 className="h-4 w-4" />
                    </button>
                    {row.Is_Active !== false && row.Is_Active !== 0 && (
                        <button
                            type="button"
                            className="btn-ghost btn-sm !px-2 text-red-600"
                            title="Deactivate"
                            onClick={(event) => {
                                event.stopPropagation();
                                if (!window.confirm(
                                    `Stop using ${row.Supplier_Name}? Past purchases are kept; `
                                    + 'it drops out of the pickers.'
                                )) return;
                                deactivate(row).catch(() => { });
                            }}
                        >
                            <FiSlash className="h-4 w-4" />
                        </button>
                    )}
                </div>
            )
        }
    ];

    return (
        <div className="page">
            <PageHeader
                title="Suppliers"
                subtitle="Who you buy from, and what you owe them"
                actions={(
                    <button
                        type="button"
                        className="btn-primary"
                        onClick={() => setEditing(blankSupplier())}
                    >
                        <FiPlus className="h-4 w-4" /> New supplier
                    </button>
                )}
            />

            <div className="grid grid-cols-2 gap-2 sm:gap-3">
                <StatCard
                    label="Suppliers"
                    value={rows.length}
                    sub={filters.includeInactive ? 'Active and retired' : 'Active'}
                    icon={FiUsers}
                    tone="brand"
                />
                <StatCard
                    label="Owed to suppliers"
                    value={`Rs. ${money(owed)}`}
                    sub={owedCount ? `${owedCount} with a balance` : 'Nothing outstanding'}
                    icon={FiDollarSign}
                    tone={owed > 0 ? 'amber' : 'slate'}
                />
            </div>

            <div className="toolbar">
                <div className="relative w-full sm:min-w-[16rem] sm:flex-1">
                    <FiSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <input
                        className="input pl-9"
                        placeholder="Search by name, code, mobile or city"
                        value={filters.search}
                        onChange={(event) => setFilters({ ...filters, search: event.target.value })}
                    />
                </div>

                <button
                    type="button"
                    className={filters.includeInactive ? 'btn-primary' : 'btn-secondary'}
                    onClick={() => setFilters({ ...filters, includeInactive: !filters.includeInactive })}
                >
                    Show retired
                </button>
            </div>

            <DataTable
                className="min-h-0 flex-1"
                columns={columns}
                rows={rows}
                loading={loading}
                rowKey={(row) => row.Supplier_ID}
                onRowClick={(row) => setEditing(toForm(row))}
                rowClassName={(row) => ((row.Is_Active === false || row.Is_Active === 0)
                    ? 'opacity-60'
                    : '')}
                emptyTitle="No suppliers yet"
                emptyDescription="Add the shops and distributors you buy from; they then appear on the purchase screen."
                emptyIcon={FiTruck}
            />

            <SupplierForm
                form={editing}
                saving={saving}
                onChange={setEditing}
                onClose={() => setEditing(null)}
                onSave={() => {
                    if (!String(editing?.supplierName || '').trim()) {
                        toast.error('A supplier needs a name.');
                        return;
                    }
                    save(editing).catch(() => { });
                }}
            />
        </div>
    );
};

/* -------------------------------- the form -------------------------------- */

const SupplierForm = ({ form, saving, onChange, onClose, onSave }) => {
    const isEdit = !!form?.Supplier_ID;
    const set = (patch) => onChange({ ...form, ...patch });

    return (
        <Modal
            open={!!form}
            onClose={onClose}
            title={isEdit ? 'Edit supplier' : 'New supplier'}
            subtitle={isEdit
                ? 'The balance moves with purchases and payments, so it is not edited here.'
                : 'Only the name is required. The rest can be filled in later.'}
            width="lg"
            dismissible={!saving}
            footer={(
                <>
                    <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="btn-primary"
                        onClick={onSave}
                        disabled={saving || !String(form?.supplierName || '').trim()}
                    >
                        {saving ? 'Saving…' : 'Save'}
                    </button>
                </>
            )}
        >
            {form && (
                <div className="space-y-3">
                    <div className="grid gap-3 sm:grid-cols-[1fr_10rem]">
                        <Field label="Name" required>
                            <Input
                                value={form.supplierName}
                                onChange={(e) => set({ supplierName: e.target.value })}
                                placeholder="Metro Cash & Carry"
                                data-autofocus
                            />
                        </Field>
                        <Field label="Code" hint="Your own reference.">
                            <Input
                                value={form.supplierCode}
                                onChange={(e) => set({ supplierCode: e.target.value })}
                            />
                        </Field>
                    </div>

                    <div className="grid gap-3 sm:grid-cols-3">
                        <Field label="Contact person">
                            <Input
                                value={form.contactPerson}
                                onChange={(e) => set({ contactPerson: e.target.value })}
                            />
                        </Field>
                        <Field label="Mobile">
                            <Input
                                value={form.mobileNo}
                                onChange={(e) => set({ mobileNo: e.target.value })}
                                placeholder="03001234567"
                            />
                        </Field>
                        <Field label="Landline">
                            <Input
                                value={form.phoneNo}
                                onChange={(e) => set({ phoneNo: e.target.value })}
                            />
                        </Field>
                    </div>

                    <div className="grid gap-3 sm:grid-cols-[1fr_12rem]">
                        <Field label="Email">
                            <Input
                                type="email"
                                value={form.email}
                                onChange={(e) => set({ email: e.target.value })}
                            />
                        </Field>
                        <Field label="City">
                            <Input
                                value={form.city}
                                onChange={(e) => set({ city: e.target.value })}
                            />
                        </Field>
                    </div>

                    <Field label="Address">
                        <Textarea
                            rows={2}
                            value={form.address}
                            onChange={(e) => set({ address: e.target.value })}
                        />
                    </Field>

                    <div className="rounded-lg bg-slate-50 p-3">
                        <p className="mb-2 text-xs text-slate-500">
                            Tax numbers are printed on the purchase record and are what a
                            sales-tax return is reconciled against.
                        </p>
                        <div className="grid gap-3 sm:grid-cols-2">
                            <Field label="NTN">
                                <Input value={form.ntn} onChange={(e) => set({ ntn: e.target.value })} />
                            </Field>
                            <Field label="STRN">
                                <Input value={form.strn} onChange={(e) => set({ strn: e.target.value })} />
                            </Field>
                        </div>
                    </div>

                    <div className="grid gap-3 sm:grid-cols-2">
                        <Field label="Credit days" hint="0 means paid on delivery.">
                            <NumberInput
                                value={form.creditDays}
                                onChange={(e) => set({ creditDays: e.target.value })}
                            />
                        </Field>

                        {!isEdit && (
                            <Field
                                label="Opening balance"
                                hint="What is already owed to them today. Set once, when adding."
                            >
                                <NumberInput
                                    value={form.openingBalance}
                                    onChange={(e) => set({ openingBalance: e.target.value })}
                                />
                            </Field>
                        )}
                    </div>

                    {isEdit && (
                        <Toggle
                            label="Active"
                            description="A retired supplier stays on past purchases but leaves the pickers."
                            checked={!!form.isActive}
                            onChange={(value) => set({ isActive: value })}
                        />
                    )}
                </div>
            )}
        </Modal>
    );
};

export default SuppliersPage;
